import { Play, Pause, Maximize, Minimize, Volume2, VolumeX } from 'lucide-react';
import { useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/cn';
import { Timeline } from './Timeline';

const PLAYBACK_RATES = [0.25, 0.5, 1, 1.5, 2] as const;

interface TimelineTask {
  id: string;
  timestamp?: number;
  taskType?: string;
}

interface MediaControlsProps {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  isFullscreen: boolean;
  playbackRate: number;
  onTogglePlay: () => void;
  onSeek: (seconds: number) => void;
  onSetRate: (rate: number) => void;
  onToggleFullscreen: () => void;
  /** Optional: task markers on timeline */
  tasks?: TimelineTask[];
  selectedTaskId?: string;
  onSelectTask?: (task: TimelineTask) => void;
  /** Hide fullscreen button (e.g. for audio) */
  hideFullscreen?: boolean;
  /** Reference to the media element for volume control */
  mediaRef?: React.RefObject<HTMLVideoElement | HTMLAudioElement | null>;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function MediaControls({
  currentTime,
  duration,
  isPlaying,
  isFullscreen,
  playbackRate,
  onTogglePlay,
  onSeek,
  onSetRate,
  onToggleFullscreen,
  tasks,
  selectedTaskId,
  onSelectTask,
  hideFullscreen,
  mediaRef,
}: MediaControlsProps) {
  const [showRateMenu, setShowRateMenu] = useState(false);
  const [muted, setMuted] = useState(false);
  const rateRef = useRef<HTMLDivElement>(null);

  const toggleMute = useCallback(() => {
    if (mediaRef?.current) {
      mediaRef.current.muted = !mediaRef.current.muted;
      setMuted(!muted);
    }
  }, [mediaRef, muted]);

  return (
    <div
      className="absolute inset-x-0 bottom-0 z-[5] flex flex-col gap-1 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-6 opacity-0 transition-opacity group-hover/player:opacity-100"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Timeline */}
      <Timeline
        currentTime={currentTime}
        duration={duration}
        onSeek={onSeek}
        tasks={tasks}
        selectedTaskId={selectedTaskId}
        onSelectTask={onSelectTask}
      />

      {/* Controls row */}
      <div className="flex items-center gap-2 text-white">
        {/* Play/Pause */}
        <button type="button" onClick={onTogglePlay} className="hover:text-white/80">
          {isPlaying ? <Pause className="size-5 fill-current" /> : <Play className="size-5 fill-current" />}
        </button>

        {/* Volume */}
        {mediaRef && (
          <button type="button" onClick={toggleMute} className="hover:text-white/80">
            {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
          </button>
        )}

        {/* Time display */}
        <span className="text-xs tabular-nums">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        <div className="flex-1" />

        {/* Playback rate */}
        <div className="relative" ref={rateRef}>
          <button
            type="button"
            className="rounded px-1.5 py-0.5 text-xs font-medium hover:bg-white/20"
            onClick={() => setShowRateMenu((v) => !v)}
          >
            {playbackRate}x
          </button>
          {showRateMenu && (
            <div className="absolute bottom-full right-0 mb-1 flex flex-col rounded-md bg-gray-900/95 py-1 shadow-lg">
              {PLAYBACK_RATES.map((rate) => (
                <button
                  key={rate}
                  type="button"
                  className={cn(
                    'px-3 py-1 text-xs hover:bg-white/10',
                    rate === playbackRate && 'text-primary font-semibold',
                  )}
                  onClick={() => {
                    onSetRate(rate);
                    setShowRateMenu(false);
                  }}
                >
                  {rate}x
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Fullscreen */}
        {!hideFullscreen && (
          <button type="button" onClick={onToggleFullscreen} className="hover:text-white/80">
            {isFullscreen ? <Minimize className="size-4" /> : <Maximize className="size-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
